import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../lib/api'
import { formatWeekRange } from '../lib/dates'
import { Markdown } from '../lib/markdown'
import type { AssistantMessage, AssistantThread, Proposal, Week } from '@shared/types'

interface Props {
  week: Week | null
  weekStart: string
  profileName: string
  /** The family is waiting for approval; the tab explains rather than asks. */
  pending: boolean
  /** So the board reflects a change the moment it is applied. */
  onWeekChanged: (week: Week) => void
}

/**
 * Prompts worth a tap. Each one is a question you would actually ask and would
 * not enjoy typing, and between them they cover the three things the data can
 * answer: this week, the pattern across weeks, and the pantry itself.
 */
const SUGGESTIONS = [
  { label: 'Review this week', prompt: 'Look over the week I have planned and tell me what you would change. Point out anything repetitive, unbalanced, or likely to come home uneaten.' },
  { label: 'What’s getting repetitive?', prompt: 'Across the recent weeks, what am I leaning on too heavily? Which pantry items am I barely using?' },
  { label: 'Fill the gaps', prompt: 'Which days this week are missing something, and what from the pantry would you put there?' },
  { label: 'Plan the week for me', prompt: 'Plan all five days from the pantry, avoiding the same main dish twice, and set them up for me. Keep anything already marked skip or pizza as it is.' },
  { label: 'Fill Tuesday', prompt: 'Put together a lunch for Tuesday from the pantry — main, fruit, veg, side and a snack — and set it.' },
  { label: 'What should I buy?', prompt: 'Based on what we keep packing and what he seems to like, what should I add to the pantry? Say plainly that these are not in it yet.' },
  { label: 'Balance check', prompt: 'Looking at this week, is there a reasonable spread across the categories? Keep it general — you are not a dietitian.' },
]

/** How many past weeks to hand the model. Enough for a pattern, not a history essay. */
const PAST_WEEKS = 6

export function AssistantPage({ week, weekStart, profileName, pending, onWeekChanged }: Props) {
  const [configured, setConfigured] = useState<boolean | null>(null)
  const [threads, setThreads] = useState<AssistantThread[]>([])
  const [threadId, setThreadId] = useState<string | null>(null)
  const [turns, setTurns] = useState<AssistantMessage[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /* What it wants to change, waiting on a tap. Never more than one at a time:
     a stale proposal from three questions ago is a trap, not a convenience. */
  const [proposal, setProposal] = useState<Proposal | null>(null)
  const [applied, setApplied] = useState<string[] | null>(null)
  const endRef = useRef<HTMLDivElement>(null)

  const refreshThreads = useCallback(async () => {
    try {
      setThreads(await api.listThreads())
    } catch {
      // The list is a convenience; asking still works without it.
    }
  }, [])

  useEffect(() => {
    api
      .assistantStatus()
      .then((r) => {
        setConfigured(r.configured)
        if (r.configured) void refreshThreads()
      })
      .catch(() => setConfigured(false))
  }, [refreshThreads])

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [turns, busy])

  async function ask(question: string) {
    const text = question.trim()
    if (!text || busy) return
    setError(null)
    setDraft('')
    /*
     * Shown straight away with a temporary id, then replaced by the thread the
     * server returns. Waiting for the round trip to show your own question
     * makes the app feel broken for the second or two it takes.
     */
    const pending: AssistantMessage = {
      id: `pending-${Date.now()}`,
      role: 'user',
      content: text,
      author: '',
      createdAt: '',
    }
    setTurns((prev) => [...prev, pending])
    setBusy(true)
    try {
      const res = await api.ask({
        threadId,
        message: text,
        weekId: week?.id ?? null,
        pastWeeks: PAST_WEEKS,
      })
      setThreadId(res.threadId)
      setTurns(res.messages)
      setThreads(res.threads)
      setProposal(res.proposal)
      setApplied(null)
    } catch (err) {
      // Left in the thread rather than shown as a toast: you want to see which
      // question failed, and the question itself stays on screen.
      setError(err instanceof ApiError ? err.message : 'The assistant could not answer.')
    } finally {
      setBusy(false)
    }
  }

  /** Apply what was proposed. The server checks all of it again first. */
  async function applyProposal() {
    if (!proposal || busy) return
    setBusy(true)
    setError(null)
    try {
      const res = await api.applyProposal({ weekId: proposal.weekId, actions: proposal.actions })
      setApplied(res.applied)
      setProposal(null)
      if (res.week) onWeekChanged(res.week)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not apply that.')
    } finally {
      setBusy(false)
    }
  }

  async function openThread(id: string) {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      // A proposal belongs to the conversation that made it.
      setProposal(null)
      setApplied(null)
      setTurns(await api.openThread(id))
      setThreadId(id)
      setShowHistory(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open that conversation.')
    } finally {
      setBusy(false)
    }
  }

  async function removeThread(id: string) {
    try {
      setThreads(await api.deleteThread(id))
      if (id === threadId) startNew()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete that conversation.')
    }
  }

  function startNew() {
    setThreadId(null)
    setTurns([])
    setError(null)
    setShowHistory(false)
  }

  if (pending) {
    return (
      <div className="empty">
        <span className="big" aria-hidden="true">
          ✨
        </span>
        <p>The assistant switches on once your family is approved.</p>
        <p className="muted" style={{ fontSize: 13.5, maxWidth: 420, margin: '0 auto' }}>
          It answers questions about {profileName}’s week, fills days from the pantry and drafts
          shopping lists. Approval is a person looking at new sign-ups, usually within a day — you’ll get
          an email. Everything else already works.
        </p>
      </div>
    )
  }

  if (configured === false) {
    return (
      <div className="empty">
        <span className="big" aria-hidden="true">
          ✨
        </span>
        <p>The assistant is switched off.</p>
        <p className="muted" style={{ fontSize: 13.5, maxWidth: 420, margin: '0 auto' }}>
          Add an <code>OPENAI_API_KEY</code> to this Worker — <code>wrangler secret put
          OPENAI_API_KEY</code>, or the Variables and Secrets section of the Worker in the
          Cloudflare dashboard — and this tab starts working. Nothing else in the app changes
          either way.
        </p>
      </div>
    )
  }

  return (
    <div className="assistant-page">
      <div className="page-head">
        <div>
          <h1>✨ Assistant</h1>
          <p>
            It can see the pantry, the saved meals, the week of{' '}
            {week ? formatWeekRange(week.startDate) : formatWeekRange(weekStart)}, and the last{' '}
            {PAST_WEEKS} weeks. Ask it about {profileName}&rsquo;s lunches, or tell it to change
            them — it proposes, and nothing happens until you tap Apply. Chats are saved and
            shared, so you both see them.
          </p>
        </div>
      </div>

      <div className="thread-bar">
        {(turns.length > 0 || threadId) && (
          <button className="btn sm" onClick={startNew} disabled={busy}>
            + New chat
          </button>
        )}
        {threads.length > 0 && (
          <button
            className="btn sm ghost"
            onClick={() => setShowHistory((v) => !v)}
            aria-expanded={showHistory}
          >
            🕘 Past chats ({threads.length})
          </button>
        )}
      </div>

      {showHistory && (
        <div className="thread-list">
          {threads.map((thread) => (
            <div className={`thread-row ${thread.id === threadId ? 'current' : ''}`} key={thread.id}>
              <button className="open" onClick={() => openThread(thread.id)} disabled={busy}>
                <span className="title">{thread.title || 'Untitled'}</span>
                <span className="sub">
                  {thread.createdBy || 'someone'} · {thread.updatedAt.slice(0, 10)} ·{' '}
                  {Math.ceil(thread.messageCount / 2)} question
                  {Math.ceil(thread.messageCount / 2) === 1 ? '' : 's'}
                </span>
              </button>
              <button
                className="btn sm ghost danger"
                onClick={() => removeThread(thread.id)}
                aria-label={`Delete ${thread.title || 'this conversation'}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {turns.length === 0 && (
        <div className="suggestions">
          {SUGGESTIONS.map((s) => (
            <button key={s.label} className="suggestion" onClick={() => ask(s.prompt)} disabled={busy}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      <div className="thread">
        {turns.map((turn) => (
          <div className={`turn ${turn.role}`} key={turn.id}>
            {/* An initial, not a truncated name: "Evan" cut to fit read as
                "Eva". The full name is on hover and for screen readers. */}
            <div className="who" title={turn.role === 'user' ? turn.author || 'You' : 'Assistant'}>
              {turn.role === 'user' ? (
                <>
                  <span aria-hidden="true">{(turn.author || 'You').charAt(0).toUpperCase()}</span>
                  <span className="sr-only">{turn.author || 'You'} asked</span>
                </>
              ) : (
                <>
                  <span aria-hidden="true">✨</span>
                  <span className="sr-only">Assistant answered</span>
                </>
              )}
            </div>
            {/* The answer is Markdown; the question is whatever was typed, and
                stays literal so it reads back exactly as it was sent. */}
            <div className="what">
              {turn.role === 'assistant' ? <Markdown text={turn.content} /> : turn.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="turn assistant">
            <div className="who">✨</div>
            <div className="what muted">Thinking…</div>
          </div>
        )}

        {proposal && (proposal.actions.length > 0 || proposal.problems.length > 0) && (
          <div className="proposal" role="group" aria-label="Proposed changes">
            {proposal.actions.length > 0 && (
              <>
                <p className="proposal-head">Apply these changes?</p>
                <ul className="proposal-list">
                  {proposal.actions.map((a, i) => (
                    <li key={i}>{a.summary}</li>
                  ))}
                </ul>
              </>
            )}
            {proposal.problems.length > 0 && (
              <ul className="proposal-problems">
                {proposal.problems.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            )}
            <div className="proposal-actions">
              <button className="btn sm ghost" onClick={() => setProposal(null)} disabled={busy}>
                Discard
              </button>
              {proposal.actions.length > 0 && (
                <button className="btn sm primary" onClick={() => void applyProposal()} disabled={busy}>
                  Apply {proposal.actions.length} change{proposal.actions.length === 1 ? '' : 's'}
                </button>
              )}
            </div>
          </div>
        )}

        {applied && applied.length > 0 && (
          <div className="proposal applied" role="status">
            <p className="proposal-head">✓ Applied</p>
            <ul className="proposal-list">
              {applied.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </div>
        )}

        {error && <div className="banner error">{error}</div>}
        <div ref={endRef} />
      </div>

      <form
        className="ask"
        onSubmit={(e) => {
          e.preventDefault()
          void ask(draft)
        }}
      >
        <input
          type="text"
          autoComplete="off"
          placeholder={turns.length ? 'Ask something else…' : 'Ask about the plan…'}
          value={draft}
          maxLength={2000}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="Ask the assistant"
          disabled={busy}
        />
        <button className="btn primary" type="submit" disabled={busy || !draft.trim()}>
          Ask
        </button>
      </form>
    </div>
  )
}
